import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { AppModalData } from 'src/types/ModalData';
import { ChangeLogProps } from 'src/components/stowage/common/changeLog/ChangeLog';
import { OperationPanelContext } from 'src/types/ViewModel/Stowage/OperationPanelContext';
import { PinnedPanel, PinnedPanelType } from 'src/types/ViewModel/Stowage/PinnedPanel';
import { UUID } from 'crypto';

type StowageUIContextType = {
    isTradeNameSelectionListOpen: boolean | null;
    setIsTradeNameSelectionListOpen: (isVisible: boolean | null) => void;
    isStowagePlanListMenuOpen: boolean | null;
    setIsStowagePlanListMenuOpen: (isVisible: boolean | null) => void;
    isCopyStowagePlanMenuOpen: boolean | null;
    setIsCopyStowagePlanMenuOpen: (isVisible: boolean | null) => void;
    isRenameStowagePlanMenuOpen: boolean | null;
    setIsRenameStowagePlanMenuOpen: (isVisible: boolean | null) => void;
    isPublishPromoteOpen: boolean | null;
    setIsPublishPromoteOpen: (isVisible: boolean | null) => void;
    isCargoDetailsPanelOpen: boolean | null;
    setIsCargoDetailsPanelOpen: (showCargo: boolean | null) => void;
    isUtilizationOpen: boolean | null;
    setIsUtilizationOpen: (isVisible: boolean | null) => void;
    isShareMenuOpen: boolean | null;
    setIsShareMenuOpen: (isVisible: boolean | null) => void;
    isFillPanelOpen: boolean | null;
    setIsFillPanelOpen: (isVisible: boolean | null) => void;
    isColumnsPanelOpen: boolean | null;
    setIsColumnsPanelOpen: (isVisible: boolean | null) => void;
    isShipFigurePanelOpen: boolean | null;
    setIsShipFigurePanelOpen: (isVisible: boolean | null) => void;
    isBlockTankPanelOpen: boolean | null;
    setIsBlockTankPanelOpen: (isVisible: boolean | null) => void;
    isAdjustCargoTemperaturePanelOpen: boolean | null;
    setIsAdjustCargoTemperaturePanelOpen: (isVisible: boolean | null) => void;
    isShowOperationForCargoPanelOpen: boolean | null;
    setIsShowOperationForCargoPanelOpen: (isVisible: boolean | null) => void;
    isCargoColorPanelOpen: boolean | null;
    setIsCargoColorPanelOpen: (isVisible: boolean | null) => void;
    isCargoOperationPanelOpen: OperationPanelContext | null;
    setIsCargoOperationPanelOpen: (panelContext: OperationPanelContext) => void;
    isSplitCargoOperationPanelOpen: boolean | null;
    setIsSplitCargoOperationPanelOpen: (isVisible: boolean | null) => void;
    isPrintPanelOpen: boolean;
    setIsPrintPanelOpen: (isOpen: boolean) => void;
    isGeneratingTankPlanReport: boolean;
    setIsGeneratingTankPlanReport: (isGenerating: boolean) => void;
    popOutOperationList: boolean;
    setPopOutOperationList: (isOpen: boolean) => void;
    stowageModalData: AppModalData | null;
    setStowageModalData: (stowageModalData: AppModalData | null) => void;
    mergeCargoPanelContext: UUID | null;
    setMergeCargoPanelContext: (cargoGuid: UUID | null) => void;
    triggerChangeLogProps: ChangeLogProps | null;
    setTriggerChangeLogProps: (props: ChangeLogProps | null) => void;
    viewCargoOperation: RotationCargoOperation | null;
    setViewCargoOperation: (cargoOperation: RotationCargoOperation | null) => void;
    pinnedPanels: PinnedPanel[];
    addPinnedPanel: (panel: PinnedPanel) => void;
    removePinnedPanel: (type: PinnedPanelType) => void;
    clearPinnedPanels: () => void;
    isPanelPinned: (type: PinnedPanelType) => boolean;
};

export const StowageUIContext = React.createContext<StowageUIContextType>(undefined!);

export const StowageUIProvider = ({ children }: ContextProps) => {
    const [isTradeNameSelectionListOpen, setIsTradeNameSelectionListOpen] = useState<boolean | null>(null);
    const [isStowagePlanListMenuOpen, setIsStowagePlanListMenuOpen] = useState<boolean | null>(null);
    const [isCopyStowagePlanMenuOpen, setIsCopyStowagePlanMenuOpen] = useState<boolean | null>(null);
    const [isRenameStowagePlanMenuOpen, setIsRenameStowagePlanMenuOpen] = useState<boolean | null>(null);
    const [isPublishPromoteOpen, setIsPublishPromoteOpen] = useState<boolean | null>(null);
    const [isCargoDetailsPanelOpen, setIsCargoDetailsPanelOpen] = useState<boolean | null>(null);
    const [isUtilizationOpen, setIsUtilizationOpen] = useState<boolean | null>(null);
    const [isShareMenuOpen, setIsShareMenuOpen] = useState<boolean | null>(null);
    const [isFillPanelOpen, setIsFillPanelOpen] = useState<boolean | null>(null);
    const [isColumnsPanelOpen, setIsColumnsPanelOpen] = useState<boolean | null>(null);
    const [isShipFigurePanelOpen, setIsShipFigurePanelOpen] = useState<boolean | null>(null);
    const [isBlockTankPanelOpen, setIsBlockTankPanelOpen] = useState<boolean | null>(null);
    const [isAdjustCargoTemperaturePanelOpen, setIsAdjustCargoTemperaturePanelOpen] = useState<boolean | null>(null);
    const [isShowOperationForCargoPanelOpen, setIsShowOperationForCargoPanelOpen] = useState<boolean | null>(null);
    const [isCargoColorPanelOpen, setIsCargoColorPanelOpen] = useState<boolean | null>(null);
    const [isCargoOperationPanelOpen, setIsCargoOperationPanelOpen] = useState<OperationPanelContext | null>(null);
    const [isSplitCargoOperationPanelOpen, setIsSplitCargoOperationPanelOpen] = useState<boolean | null>(null);
    const [isPrintPanelOpen, setIsPrintPanelOpen] = useState<boolean>(false);
    const [isGeneratingTankPlanReport, setIsGeneratingTankPlanReport] = useState<boolean>(false);
    const [popOutOperationList, setPopOutOperationList] = useState<boolean>(false);
    const [stowageModalData, setStowageModalData] = useState<AppModalData | null>(null);
    const [mergeCargoPanelContext, setMergeCargoPanelContext] = useState<UUID | null>(null);
    const [triggerChangeLogProps, setTriggerChangeLogProps] = useState<ChangeLogProps | null>(null);
    const [viewCargoOperation, setViewCargoOperation] = useState<RotationCargoOperation | null>(null);
    const [pinnedPanels, setPinnedPanels] = useState<PinnedPanel[]>([]);

    const addPinnedPanel = useCallback((panel: PinnedPanel) => {
        setPinnedPanels((prev) => {
            if (prev.some((p) => p.type === panel.type)) return prev;
            if (prev.length >= 3) return prev;
            return [...prev, panel];
        });
    }, []);

    const removePinnedPanel = useCallback((type: PinnedPanelType) => {
        setPinnedPanels((prev) => prev.filter((p) => p.type !== type));
    }, []);

    const clearPinnedPanels = useCallback(() => {
        setPinnedPanels([]);
    }, []);

    const isPanelPinned = useCallback(
        (type: PinnedPanelType) => {
            return pinnedPanels.some((p) => p.type === type);
        },
        [pinnedPanels],
    );

    const value = useMemo(
        () => ({
            isTradeNameSelectionListOpen,
            setIsTradeNameSelectionListOpen,
            isStowagePlanListMenuOpen,
            setIsStowagePlanListMenuOpen,
            isCopyStowagePlanMenuOpen,
            setIsCopyStowagePlanMenuOpen,
            isRenameStowagePlanMenuOpen,
            setIsRenameStowagePlanMenuOpen,
            isPublishPromoteOpen,
            setIsPublishPromoteOpen,
            isCargoDetailsPanelOpen,
            setIsCargoDetailsPanelOpen,
            isUtilizationOpen,
            setIsUtilizationOpen,
            isShareMenuOpen,
            setIsShareMenuOpen,
            isFillPanelOpen,
            setIsFillPanelOpen,
            isColumnsPanelOpen,
            setIsColumnsPanelOpen,
            isShipFigurePanelOpen,
            setIsShipFigurePanelOpen,
            isBlockTankPanelOpen,
            setIsBlockTankPanelOpen,
            isAdjustCargoTemperaturePanelOpen,
            setIsAdjustCargoTemperaturePanelOpen,
            isShowOperationForCargoPanelOpen,
            setIsShowOperationForCargoPanelOpen,
            isCargoColorPanelOpen,
            setIsCargoColorPanelOpen,
            isCargoOperationPanelOpen,
            setIsCargoOperationPanelOpen,
            isSplitCargoOperationPanelOpen,
            setIsSplitCargoOperationPanelOpen,
            isPrintPanelOpen,
            setIsPrintPanelOpen,
            isGeneratingTankPlanReport,
            setIsGeneratingTankPlanReport,
            popOutOperationList,
            setPopOutOperationList,
            stowageModalData,
            setStowageModalData,
            mergeCargoPanelContext,
            setMergeCargoPanelContext,
            triggerChangeLogProps,
            setTriggerChangeLogProps,
            viewCargoOperation,
            setViewCargoOperation,
            pinnedPanels,
            addPinnedPanel,
            removePinnedPanel,
            clearPinnedPanels,
            isPanelPinned,
        }),
        [
            isTradeNameSelectionListOpen,
            isStowagePlanListMenuOpen,
            isCopyStowagePlanMenuOpen,
            isRenameStowagePlanMenuOpen,
            isPublishPromoteOpen,
            isCargoDetailsPanelOpen,
            isUtilizationOpen,
            isShareMenuOpen,
            isFillPanelOpen,
            isColumnsPanelOpen,
            isShipFigurePanelOpen,
            isBlockTankPanelOpen,
            isAdjustCargoTemperaturePanelOpen,
            isShowOperationForCargoPanelOpen,
            isCargoColorPanelOpen,
            isCargoOperationPanelOpen,
            isSplitCargoOperationPanelOpen,
            isPrintPanelOpen,
            isGeneratingTankPlanReport,
            popOutOperationList,
            stowageModalData,
            mergeCargoPanelContext,
            triggerChangeLogProps,
            viewCargoOperation,
            pinnedPanels,
            addPinnedPanel,
            removePinnedPanel,
            clearPinnedPanels,
            isPanelPinned,
        ],
    );

    return <StowageUIContext.Provider value={value}>{children}</StowageUIContext.Provider>;
};

export const useStowageUIStore = () => useContext(StowageUIContext);
